import messageTags from '@/helpers/lists/messageTags';
import { escape } from 'html-escaper';
import { swapCustomFieldIdWithCustomFieldName } from '@/helpers/quickRepliesParser';

function escapeHtml(text) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };

  return text.replace(/[&<>"']/g, m => map[m]);
}

function unescapeHtml(text) {
  return text
    .replaceAll('&amp;', '&')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&quot;', '"')
    .replaceAll('&#039;', "'");
}

/**
 * Method inserts string into another string on a desired position.
 *
 * @param {*} target String that needs to be extended
 * @param {*} newString String that is going to be inserted
 * @param {*} pos Position in the target where insertion should occur
 */
function insert(target, newString = '', pos = 0) {
  const safePos = pos < 0 ? 0 : pos > target.length ? target.length : pos;
  return target.slice(0, safePos) + newString + target.slice(safePos);
}

function isRightAlignedText(text) {
  text = text.replace(/\s+/g, '');
  const nonArabicText = text.replace(/[\u0600-\u06FF\u0750-\u077F\u0590-\u05FF]/g, '');

  return ((nonArabicText.length / text.length) * 100) < 50;
}

function removeExtraSpaces(text) {
  return text.replace(/[ ]+/, ' ');
}

/**
 * Methods converts html content from any input and transforms it into whatsapp format for bold, italic and strikethrough text styles.
 */
function whatsappFormatMessage(rawMessage, temp = false) {
  if (typeof rawMessage !== 'string') {
    return '';
  }

  /* eslint-disable no-multi-spaces */
  let convertedMessage = rawMessage
    .replace(/<p>(.*?)<\/p>/g, '$1\n')        // convert p tags to newline
    .replace(/(<\/?strong(?: .*?)?>)/g, '*')  // bold
    .replace(/(<\/?b(?: .*?)?>)/g, '*')       // bold
    .replace(/(<\/?em(?: .*?)?>)/g, '_')      // italic
    .replace(/(<\/?s(?: .*?)?>)/g, '~')       // strikethrough
    .replace(/(<code(?: .*?)?>)/g, '{{')      // start of the code block which we are using to present the tag item
    .replace(/(<\/code(?: .*?)?>\s{0,1})/g, '}}');    // end of the code block
  /* eslint-enable no-multi-spaces */

  convertedMessage = convertedMessage.replaceAll(/\*(\s?)(.*?)(\s?)\*/g, '$1*$2*$3');
  convertedMessage = convertedMessage.replaceAll(/_(\s?)(.*?)(\s?)_/g, '$1_$2_$3');
  convertedMessage = convertedMessage.replaceAll(/~(\s?)(.*?)(\s?)~/g, '$1~$2~$3');

  if (temp) {
    // This masks a placeholder tag so it doesnt get removed before we apply custom fields. Then it has to be reverted before rendering to DOM.
    convertedMessage = convertedMessage
      .replace(/<span class="editor-tag ellipsis">(.*?)<\/span>/g, '{!{$1}!}');
  }

  convertedMessage = convertedMessage.replace(/(<([^>]+)>)/gi, ''); // any other markup

  // Return some characters back to human readable form
  convertedMessage = convertedMessage
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  // .replace(/({{(\w+|\s)+}}) /g, '$1'); // removes additional space after tag element

  // Removes all the spaces around including the new line at the end.
  convertedMessage = convertedMessage.trim();

  return convertedMessage;
}

function replaceUnderscore(text) {
  return text.replaceAll('_', ' ');
}

function whatsAppFormatToHTML(whatsAppMessage, swapCustomFields = true) {
  if (swapCustomFields) whatsAppMessage = swapCustomFieldIdWithCustomFieldName(whatsAppMessage);
  /* eslint-disable no-multi-spaces */
  return whatsAppMessage
    .replace(/(.*?)\\r\\n/g, '$1<br>')              // convert newline to p tag
    .replace(/(.*)\n/g, '<p>$1</p>')                // convert newline to p tag
    .replace(/\*(.*?)\*/g, '<b>$1</b>')             // bold
    .replace(/_(.*?)_/g, '<em>$1</em>')             // italic
    .replace(/~(.*?)~/g, '<s>$1</s>')               // strikethrough
    .replace(/\{\{(.*?)\}\}/g, '<code>$1</code>');  // tag
  /* eslint-enable no-multi-spaces */
}

/** Not used anywhere but probably needed for parsing tag places in templates */
function getTagTemplate(str) {
  str = str.replace(/\{\{(.*?)\}\}/ig, '<input type="button" class="editor-tag ellipsis" data-name="{{$1}}" value="$1"/>&nbsp;');

  messageTags.forEach((tag) => {
    str = str.replace(new RegExp(`value="${tag.keyword}"`, 'g'), `value="${tag.name}"`);
  });

  return str;
}

/**
 * Methods converts whatsapp format for bold, italic and strikethrough text styles into quill content object.
 */
function quillFormatMessage(str, options = {}) {
  const quillContentArray = [];
  // const rowRegex = /(.*\n*)/g;
  const regex = /\*(.*?)\*|\{\{\*?(.*?)\*?\}\}|_(.*?)_|~(.*?)~|(.+?(?=[~*_{}\n]|$))/g;

  // First break by newline
  // const rowMatches = str.match(rowRegex);
  const rowMatches = str.split('\n');

  // Then run other matches
  if (!rowMatches) {
    return null;
  }
  rowMatches.forEach((rowMatch) => {
    const matches = rowMatch.match(regex);

    if (!matches) {
      quillContentArray.push({
        insert: '\n',
      });
      return;
    }
    matches.forEach((match) => {
      if (/\{\{\*(.*?)\*\}\}/.test(match)) {
        if (options.noTags === false) {
          quillContentArray.push({
            insert: match.replace(/^\{\{\*(.*?)\*\}\}$/, (match, p1) => p1),
            attributes: {
              code: true,
              bold: true,
            },
          });
        } else {
          quillContentArray.push({
            insert: match,
          });
        }
      } else if (/\{\{(.*?)\}\}/.test(match)) {
        if (options.noTags === false) {
          quillContentArray.push({
            insert: match.replace(/^\{\{(.*?)\}\}$/, (match, p1) => p1),
            attributes: {
              code: true,
            },
          });
        } else {
          quillContentArray.push({
            insert: match,
          });
        }
      } else if (/\*(.*?)\*/.test(match)) {
        quillContentArray.push({
          insert: match.replace(/^\*(.*)\*$/, (match, p1) => p1),
          attributes: {
            bold: true,
          },
        });
      } else if (/_(.*?)_/.test(match)) {
        quillContentArray.push({
          insert: match.replace(/^_(.*?)_$/, (match, p1) => p1),
          attributes: {
            italic: true,
          },
        });
      } else if (/~(.*?)~/.test(match)) {
        quillContentArray.push({
          insert: match.replace(/^~(.*?)~$/, (match, p1) => p1),
          attributes: {
            strike: true,
          },
        });
      } else {
        quillContentArray.push({
          insert: match,
        });
      }
    });

    quillContentArray.push({
      insert: '\n',
    });
  });

  return quillContentArray;
}

/** Converts all tags in a string e.g. "{{contact name}}"  */
function convertTagToUnderlineVersion(str) {
  const regex = /(<code>)(.*?)(<\/code>)/g;
  const matches = str.matchAll(regex);
  const matchArray = [...matches];

  matchArray.forEach((match) => {
    const underscoreTag = match[2].replace(/ /, '_');
    str = str.replace(match[0], `${match[1]}${underscoreTag}${match[3]}`);
  });

  return str;
}

/** Converts _ to space  */
function convertUnderscoreToSpace(str) {
  return str.replace('_', ' ');
}

/**
 * Converts all tag representation ( {{2}} ) into html tag view.
 * It sanitizes user provided input for the template content and then replaces the safe tag places so we can use this to inject the value via v-html safely.
 */
function convertToTagView(inputText) {
  return escape(inputText).replace(/\{\{(\d+)\}\}/g, '<span class="template-tag">$1</span>');
}

/**
 * Used on tables when we want to show the content but without newline breaks.
 * Code removes p tags to avoid line breaks.
 */
function flattenHTML(htmlText) {
  return htmlText.replace(/<p>(.*?)<\/p>/g, '$1 ');
}


export {
  escapeHtml,
  unescapeHtml,
  flattenHTML,
  insert,
  isRightAlignedText,
  removeExtraSpaces,
  whatsappFormatMessage,
  whatsAppFormatToHTML,
  quillFormatMessage,
  convertToTagView,
  convertTagToUnderlineVersion,
  convertUnderscoreToSpace,
};
